/* API client for the workbench (agent bundles v2), execution settings, the
   Lawyer Review Tray, and v2 executor runs. */
import { apiFetch, BASE, safeFetch } from "./client";
import type { AgentDefV2, BundleDetailV2, BundleMapV2, WorkbenchMeta } from "../workbench/types";
import type { LiveOrchestratorState } from "./triage";

export async function getWorkbenchMeta(): Promise<WorkbenchMeta | null> {
  return safeFetch<WorkbenchMeta>("/agent-bundles/workbench-meta");
}

export async function getBundleV2(id: string): Promise<BundleDetailV2 | null> {
  const r = await safeFetch<{ bundle: BundleDetailV2 }>(
    `/agent-bundles/${encodeURIComponent(id)}?v2=1`,
  );
  return r?.bundle ?? null;
}

export interface FlatVersion {
  bundleId: string;
  bundleName: string;
  versionId: string;
  label: string;
  agentCount: number;
  createdAt: string;
}

export async function listAllVersions(): Promise<FlatVersion[]> {
  const r = await safeFetch<{ versions: FlatVersion[] }>("/agent-bundles/versions");
  return r?.versions ?? [];
}

export async function validateAgent(
  def: AgentDefV2,
): Promise<{ problems: string[]; firesWhen: string } | null> {
  return safeFetch("/agent-bundles/validate-agent", {
    method: "POST",
    body: JSON.stringify(def),
  });
}

export async function createBundleV2(body: {
  name: string;
  note?: string;
  seedSpine?: boolean;
}): Promise<{ id: string } | null> {
  const r = await safeFetch<{ bundle: { id: string } }>("/agent-bundles", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return r?.bundle ?? null;
}

export async function replaceDraftV2(id: string, draft: BundleMapV2): Promise<boolean> {
  const r = await safeFetch(`/agent-bundles/${encodeURIComponent(id)}/draft`, {
    method: "PUT",
    body: JSON.stringify(draft),
  });
  return r != null;
}

export async function consolidateRahafDraft(id: string): Promise<{ agentCount: number } | null> {
  return safeFetch(`/agent-bundles/${encodeURIComponent(id)}/consolidate-rahaf`, { method: "POST" });
}

/* ---- execution settings ---- */

export interface ExecutionSettings {
  semanticEvaluatorModel: string;
  dedupModel: string;
  reviewTrayDiscussionModel: string;
  modelAllowlist: string[];
  modelCatalogVersion: number;
  costCapUsd: number;
  /** Max concurrent LLM calls in the fan-out phase. Defaults to 10. */
  concurrencyLimit: number;
}

export async function getExecutionSettings(): Promise<ExecutionSettings | null> {
  const r = await safeFetch<{ settings: ExecutionSettings }>("/settings/execution");
  return r?.settings ?? null;
}

export async function saveExecutionSettings(
  patch: Partial<ExecutionSettings>,
): Promise<ExecutionSettings | null> {
  const r = await safeFetch<{ settings: ExecutionSettings }>("/settings/execution", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  return r?.settings ?? null;
}

/* ---- Lawyer Review Tray ---- */

export interface TrayItem {
  id: string;
  wsId: string;
  matters: string[];
  agentId: string;
  kind: "decision" | "choice" | "input" | "notice" | string;
  gate: boolean;
  holdField: string | null;
  title: string;
  detail: string;
  options: string[] | null;
  status: "pending" | "resolved" | "superseded" | "expired" | string;
  resolution: Record<string, unknown> | null;
  recurrence: Array<Record<string, unknown>>;
  justification: string | null;
  emailLocalId: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export async function listTray(q: {
  matterId?: string;
  wsId?: string;
  runScope?: string;
  status?: string;
}): Promise<TrayItem[]> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) params.set(k, v);
  const r = await safeFetch<{ items: TrayItem[] }>(`/tray?${params.toString()}`);
  return r?.items ?? [];
}

export interface TrayResolveResult {
  item: TrayItem;
  writeBack: { field: string; value: string } | null;
  staleFields: string[];
}

export async function resolveTrayItem(
  id: string,
  action: "approve" | "reject" | "choose" | "answer" | "acknowledge",
  extra: { choice?: string; text?: string } = {},
): Promise<TrayResolveResult | null> {
  return safeFetch<TrayResolveResult>(`/tray/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ action, ...extra }),
  });
}

export async function recordRuling(id: string, text: string): Promise<boolean> {
  const r = await safeFetch(`/tray/${encodeURIComponent(id)}/ruling`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  return r != null;
}

export interface TrayDiscussionTurn {
  role: "user" | "assistant";
  content: string;
}

export interface TrayDiscussionResult {
  answer: string;
  model: string;
  usage: { input_tokens: number; output_tokens: number; cost: number };
}

export function discussTrayItem(
  id: string,
  question: string,
  history: TrayDiscussionTurn[],
): Promise<TrayDiscussionResult> {
  return apiFetch<TrayDiscussionResult>(`/tray/${encodeURIComponent(id)}/discuss`, {
    method: "POST",
    body: JSON.stringify({ question, history }),
  });
}

export async function clearTrayScope(runScope: string): Promise<boolean> {
  const r = await safeFetch(`/tray?runScope=${encodeURIComponent(runScope)}`, {
    method: "DELETE",
  });
  return r != null;
}

/* ---- v2 executor runs (SSE) ---- */

export interface ExecCheckRecord {
  id: string;
  name?: string;
  wsId?: string;
  skipped: boolean;
  ok: boolean;
  parked?: boolean;
  conf: number;
  out: string;
  result: Record<string, unknown> | null;
  model: string;
  latencyMs: number;
  usage: { input_tokens: number; output_tokens: number; cost: number };
  reason?: string;
  raw?: string;
  cached?: boolean;
  trace?: {
    gate: Array<{ row: string; t: "det" | "sem"; pass: boolean; why: string }>;
    systemPrompt?: string;
    userPrompt?: string;
    problems?: string[];
    effects: string[];
  };
}

export interface ExecResult {
  triageRunId: string;
  degraded?: boolean;
  bundle: { id: string; name: string; versionId: string | null; versionLabel: string };
  checks: ExecCheckRecord[];
  routing: { workstreamId: string | null; confidence: number };
  routes: Array<{ workstreamId: string; confidence: number }>;
  perMatter: Array<{ wsId: string; confidence: number; facts: Array<[string, string, string, string]>; cost: number }>;
  facts: Array<[string, string, string, string]>;
  tray: TrayItem[];
  superseded: string[];
  saDispatches: Array<{ agentId: string; ok: boolean; runId?: string | null; summary?: string; error?: string }>;
  warnings: string[];
  usage: { input_tokens: number; output_tokens: number; cost: number; calls: number };
  updatedState: LiveOrchestratorState;
}

export interface ExecuteBody {
  bundleVersionId?: string;
  bundleId?: string;
  workstreams?: Array<{
    id: string;
    name: string;
    code?: string | null;
    entities?: Array<{
      id?: string;
      name: string;
      kind: string;
      people?: Array<{ name: string; email: string; role: string }>;
    }>;
  }>;
  state?: LiveOrchestratorState;
  localId?: string | number;
  runScope?: string;
  trace?: boolean;
}

const STREAM_IDLE_TIMEOUT_MS = 90_000;

/* Same transport as streamTriage (POST + SSE-over-fetch with the stall
   watchdog); typed for the v2 executor payloads. */
export interface ExecStartEvent {
  id: string;
  name?: string;
  wsId?: string;
}

export async function streamExecute(
  emailId: string,
  body: ExecuteBody,
  onCheck: (check: ExecCheckRecord) => void,
  signal?: AbortSignal,
  onStart?: (start: ExecStartEvent) => void,
): Promise<ExecResult> {
  const ctrl = new AbortController();
  let stalled = false;
  const onExternalAbort = (): void => ctrl.abort();
  if (signal) {
    if (signal.aborted) ctrl.abort();
    else signal.addEventListener("abort", onExternalAbort, { once: true });
  }
  const stallError = (): Error =>
    new Error(
      `no progress from the pipeline for ${STREAM_IDLE_TIMEOUT_MS / 1000}s — the backend may be stalled or the connection dropped`,
    );
  const withIdleTimeout = async <T,>(step: Promise<T>): Promise<T> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const idle = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        stalled = true;
        reject(stallError());
        ctrl.abort();
      }, STREAM_IDLE_TIMEOUT_MS);
    });
    try {
      return await Promise.race([step, idle]);
    } finally {
      clearTimeout(timer);
    }
  };
  let r: Response;
  try {
    r = await withIdleTimeout(
      fetch(`${BASE}/triage/${encodeURIComponent(emailId)}/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      }),
    );
  } catch (e) {
    if (signal) signal.removeEventListener("abort", onExternalAbort);
    if (stalled && !signal?.aborted) throw stallError();
    throw e;
  }
  if (!r.ok || !r.body) {
    if (signal) signal.removeEventListener("abort", onExternalAbort);
    throw new Error(`run stream failed — HTTP ${r.status}`);
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let complete: ExecResult | null = null;
  let streamError: string | null = null;
  const handleFrame = (frame: string): void => {
    let event = "message";
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
    }
    if (!dataLines.length) return;
    let data: unknown;
    try {
      data = JSON.parse(dataLines.join("\n"));
    } catch {
      return;
    }
    if (event === "check") onCheck(data as ExecCheckRecord);
    else if (event === "start") onStart?.(data as ExecStartEvent);
    else if (event === "complete") complete = data as ExecResult;
    else if (event === "error") streamError = (data as { message?: string }).message || "run failed";
  };
  try {
    for (;;) {
      const { done, value } = await withIdleTimeout(reader.read());
      if (value) buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        handleFrame(buf.slice(0, idx));
        buf = buf.slice(idx + 2);
      }
      if (done) break;
    }
  } catch (e) {
    if (stalled && !signal?.aborted) throw stallError();
    if (signal?.aborted || (e instanceof Error && e.name === "AbortError")) {
      const err = new Error("cancelled");
      err.name = "AbortError";
      throw err;
    }
    throw e;
  } finally {
    if (signal) signal.removeEventListener("abort", onExternalAbort);
  }
  if (streamError) throw new Error(streamError);
  if (!complete) throw new Error("run stream ended without a result");
  return complete;
}
